import { useCallback, useEffect, useRef, useState } from "react";
import Typography from "../Typography/Typography";
import { FiltersComponent } from "../FiltersComponent/FiltersComponent";
import { useNavigate } from "react-router-dom";
import useOrganisation from "../../store/Querys/useOrganisation";
import useStudentsStats from "../../store/Querys/useStudentsStats";
import useExportStudents from "../../store/Querys/useExportStudents";
import { useDimensions } from "../../lib/useDimensions";
import GridLGHidden from "../GridComponent/components/GridLGHidden";
import Button from "../Button/Button";
import ExportIcon from "../../assets/icons/bx-export-blue.svg";

const filterOptions = [
  { value: "email", label: "Email" },
  { value: "firstName", label: "First name" },
  { value: "lastName", label: "Last name" },
];

export interface IFilters {
  email?: string;
  firstName?: string;
  lastName?: string;
}

const IndividualStudentsStatisticsList = () => {
  const [filtersCount, setFiltersCount] = useState<IFilters>({});
  const [activeFilters, setActiveFilters] = useState<IFilters>({});
  const [isSearching, setIsSearching] = useState(false);
  const ITEMS_PER_PAGE = 10;
  const isMobile = useDimensions();
  const navigate = useNavigate();
  const observerRef = useRef<IntersectionObserver | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const [clearFilter, setClearFilter] = useState(false);

  const { data: adminData } = useOrganisation();
  const organizationId = adminData?.organizations?.[0]?.id;

  const { mutate: exportStudents, isPending: isExporting } =
    useExportStudents();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useStudentsStats(organizationId, {
      ...activeFilters,
      limit: ITEMS_PER_PAGE,
    });

  const handleExport = () => {
    if (!organizationId) return;

    exportStudents(organizationId, {
      onSuccess: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "students-export.zip";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      onError: (error) => {
        console.error("Export failed:", error);
      },
    });
  };

  const handleSearch = () => {
    setIsSearching(true);
    setActiveFilters(filtersCount);

    setTimeout(() => {
      setIsSearching(false);
    }, 3000);
  };

  useEffect(() => {
    if (!isLoading && isSearching) {
      setIsSearching(false);
    }
  }, [isLoading, isSearching]);

  useEffect(() => {
    setActiveFilters({});
    setClearFilter(false);
  }, [clearFilter]);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(handleObserver);
    if (bottomRef.current) observerRef.current.observe(bottomRef.current);
    return () => observerRef.current?.disconnect();
  }, [handleObserver]);

  return (
    <div className="rounded-20 border border-secondary bg-white p-12 pt-24 lg:p-24 max-lg:mb-[100px] mt-24">
      <Typography type="Header3">Individual student overview</Typography>

      <div className="flex items-center justify-between gap-24">
        <FiltersComponent
          setFiltersCount={setFiltersCount}
          filterOptions={filterOptions}
          filtersCount={filtersCount}
          onSearch={handleSearch}
          clearFilter={() => {
            setClearFilter(true);
            setFiltersCount({});
            handleSearch();
          }}
        />
        <Button
          buttonType="secondary"
          title={isExporting ? "Exporting..." : "Export"}
          testID="export"
          styles="!w-[100px] !h-[40px] !p-0 shrink-0"
          onClick={handleExport}
          disabled={isExporting || !organizationId}
          loading={isExporting}
          icon={<img src={ExportIcon} alt="Export" className="w-16 h-16 " />}
        />
      </div>

      <table className="border-collapse w-full  mt-24 max-lg:mt-16 rounded-2xl outline outline-1 outline-secondary overflow-hidden">
        <thead className="w-full">
          <tr className="bg-[#F7F7FD] border-b border-secondary">
            <th data-type="text-short" className="p-24 max-lg:p-12 text-left">
              <Typography type="Body2" className="max-lg:hidden">
                First name
              </Typography>
              <Typography type="Body2" className="lg:hidden">
                Student
              </Typography>
            </th>
            <th data-type="text-short" className="p-24 text-left max-lg:hidden">
              <Typography type="Body2">Last name</Typography>
            </th>
            <th data-type="text-short" className="p-24 text-left max-lg:hidden">
              <Typography type="Body2">Email</Typography>
            </th>
            <th
              data-type="text-short"
              className="p-24  text-left max-lg:hidden"
            >
              <Typography type="Body2">Practice time</Typography>
            </th>
          </tr>
        </thead>
        <tbody>
          {(isSearching || isLoading) &&
          (!data?.pages[0]?.data || data.pages[0].data.length === 0) ? (
            <tr className="w-full text-center">
              <td colSpan={4} className="p-24">
                <Typography type="Body2">Loading...</Typography>
              </td>
            </tr>
          ) : data?.pages[0]?.data && data.pages[0].data.length > 0 ? (
            data.pages.map((page) =>
              page.data.map((student) => (
                <tr
                  key={student.id}
                  className="border-b border-secondary cursor-pointer hover:bg-[#F7F7FD] transition-all transition-400"
                  onClick={() =>
                    navigate(
                      `/student-overview/${student.id}?name=${student.firstName}&lastName=${student.lastName}`
                    )
                  }
                >
                  <td className="p-24 max-lg:p-12 max-w-[200px]">
                    <Typography
                      type="Body2"
                      className="line-clamp-1 w-full whitespace-normal break-words"
                    >
                      {student.firstName}{" "}
                      {isMobile ? " " + student.lastName : ""}
                    </Typography>
                    <GridLGHidden>
                      <Typography
                        type="Body3"
                        className="!text-[#868686] line-clamp-1 w-full whitespace-normal break-words"
                      >
                        {student.email}
                      </Typography>
                    </GridLGHidden>
                  </td>
                  <td className="p-24 max-lg:hidden max-w-[200px]">
                    <Typography type="Body2">{student.lastName}</Typography>
                  </td>
                  <td className="p-24 max-lg:hidden max-w-[200px]">
                    <Typography
                      type="Body2"
                      className="!text-[#868686] line-clamp-1 w-full whitespace-normal break-words"
                    >
                      {student.email}
                    </Typography>
                  </td>
                  <td className="p-24  max-lg:hidden">
                    <Typography type="Body2" className="!text-[#868686]">
                      {`${
                        student.practiceTime / 86400 >= 1
                          ? `${Math.floor(student.practiceTime / 86400)} d, `
                          : ""
                      }${
                        (student.practiceTime % 86400) / 3600 >= 1
                          ? `${Math.floor(
                              (student.practiceTime % 86400) / 3600
                            )} h, `
                          : ""
                      }${
                        (student.practiceTime % 3600) / 60 >= 1
                          ? Math.ceil((student.practiceTime % 3600) / 60)
                          : "0"
                      } minutes`}
                    </Typography>
                  </td>
                </tr>
              ))
            )
          ) : (
            <tr className="w-full text-center">
              <td colSpan={4} className="p-24">
                <Typography type="Body2">No active students</Typography>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div ref={bottomRef} />
    </div>
  );
};

export default IndividualStudentsStatisticsList;
